import { useStore } from '@/store';
import { ref } from '@vue/composition-api';

// hooks
import { useAxios } from '@/vue3/hooks/axiosHook';

// models
import { AccountSummary, Account } from '@/vue3/api/models';

export const useAccountService = () => {
    // hooks
    const { apiUrl } = useStore().state;
    const axios = useAxios();
    const accounts = ref<Account[]>([]);

    // methods
    const getAllAccount = async (): Promise<Account[]> => {
        const res = await axios.get(`${apiUrl}/account`);
        accounts.value = res.data;
        return res.data;
    };

    const getAllAccountByOrganization = async (): Promise<Account[]> => {
        const res = await axios.get(`${apiUrl}/account/organization`);
        return res.data;
    };

    const getAllAccountSummary = async (): Promise<AccountSummary[]> => {
        const res = await axios.get(`${apiUrl}/account/summary`);
        return res.data;
    };

    const getAccount = async (id: number): Promise<Account> => {
        const res = await axios.get(`${apiUrl}/account/${id}`);
        return res.data;
    };

    // eslint-disable-next-line
    const getAccountAvatar = async (): Promise<any> => {
        const res = await axios.get(`${apiUrl}/account/avatar`);
        return res.data;
    };

    const createAccount = async (account: Account): Promise<Account> => {
        const res = await axios.post(`${apiUrl}/account`, account);
        await getAllAccount();
        await getAllAccountSummary();
        return res.data;
    };

    const updateAccount = async (account: Account): Promise<Account> => {
        const res = await axios.put(`${apiUrl}/account`, account);
        await getAllAccount();
        await getAllAccountSummary();
        return res.data;
    };

    // eslint-disable-next-line
    const updateAccountAvatar = async (avatar: any): Promise<any> => {
        const res = await axios.put(`${apiUrl}/account/avatar`, avatar);
        return res.data;
    };

    // eslint-disable-next-line
    const updateAccountAccessUntil = async (account: Account): Promise<any> => {
        const res = await axios.put(`${apiUrl}/account/access`, account);
        await getAllAccount();
        await getAllAccountSummary();
        return res.data;
    };

    const deleteAccount = async (id: number): Promise<Account> => {
        const res = await axios.deleteId(`${apiUrl}/account/${id}`);
        await getAllAccount();
        await getAllAccountSummary();
        return res.data;
    };

    const addAccountToMultiOrgAccount = async (
        masterAccountId: number,
        organizationId: number,
        roleId: number,
    ) => {
        const res = await axios.post(`${apiUrl}/account/multi-org/add`, {
            masterAccountId,
            organizationId,
            roleId,
        });
        if (res.status == 200 || res.status == 201) {
            return true;
        }
        return false;
    };

    const updateMultiOrgAccountActiveState = async (
        masterAccountId: number,
        secondaryAccountId: number,
        isActive: boolean,
    ) => {
        const res = await axios.post(
            `${apiUrl}/account/multi-org/update-active-state`,
            { masterAccountId, id: secondaryAccountId, isActive },
        );
        if (res.status == 200 || res.status == 201) {
            return true;
        }
        return false;
    };

    const getMultiOrgAccountList = async () => {
        const res = await axios.get(`${apiUrl}/account/multi-org/list`);
        if (res.status == 200) {
            return res.data;
        }
        return [];
    };

    const getMultiOrgAccountListByAccountId = async (account_id: number) => {
        const res = await axios.get(
            `${apiUrl}/account/multi-org/list/${account_id}`,
        );
        if (res.status == 200) {
            return res.data;
        }
        return [];
    };

    return {
        accounts,
        getAllAccount,
        getAllAccountByOrganization,
        getAllAccountSummary,
        getAccount,
        getAccountAvatar,
        createAccount,
        updateAccount,
        updateAccountAvatar,
        updateAccountAccessUntil,
        deleteAccount,
        addAccountToMultiOrgAccount,
        getMultiOrgAccountList,
        getMultiOrgAccountListByAccountId,
        updateMultiOrgAccountActiveState,
    };
};
